import type { WasteCard } from '../data/cards';

/**
 * Camera preview + prompt, with a card-number grid fallback for when a scan won't cooperate
 * (blocked school-tablet cameras, lamination glare). Buttons show only the printed card number —
 * never the answer. The grid stays open across cards so a camera-less classroom isn't forced to
 * re-open it for every single card.
 */
export class ScanView {
  private readonly root = this.getElement('#scan-view');
  private readonly video = this.getElement<HTMLVideoElement>('#scan-video');
  private readonly prompt = this.getElement('#scan-prompt');
  private readonly manualToggle = this.getElement('#manual-select-toggle');
  private readonly manualPanel = this.getElement('#manual-select');
  private readonly manualGrid = this.getElement('#manual-select-grid');
  private readonly cardButtons = new Map<string, HTMLButtonElement>();

  constructor(
    private readonly cards: WasteCard[],
    onManualSelect: (card: WasteCard) => void,
    private readonly isCardSelectable: (card: WasteCard) => boolean,
  ) {
    cards.forEach((card, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'card-number-button';
      button.textContent = String(index + 1).padStart(2, '0');
      button.setAttribute('aria-label', `카드 ${index + 1}번 ${card.nameKo}`);
      button.addEventListener('click', () => onManualSelect(card));
      this.cardButtons.set(card.id, button);
      this.manualGrid.append(button);
    });

    this.manualToggle.addEventListener('click', () => {
      this.manualPanel.hidden = !this.manualPanel.hidden;
      if (!this.manualPanel.hidden) this.refreshAvailability();
    });
  }

  get videoElement(): HTMLVideoElement {
    return this.video;
  }

  show(): void {
    this.root.hidden = false;
    if (!this.manualPanel.hidden) this.refreshAvailability();
  }

  hide(): void {
    this.root.hidden = true;
  }

  /** Camera couldn't start (blocked school tablet etc.) — say so and open the number grid. */
  showCameraError(): void {
    this.prompt.textContent = '카메라를 켤 수 없어요. 아래에서 카드 번호를 골라 주세요.';
    this.manualPanel.hidden = false;
    this.refreshAvailability();
  }

  /** Dim cards that can't be played right now (already answered, or outside the retry round). */
  private refreshAvailability(): void {
    for (const card of this.cards) {
      const button = this.cardButtons.get(card.id);
      if (button) button.disabled = !this.isCardSelectable(card);
    }
  }

  private getElement<T extends HTMLElement = HTMLElement>(selector: string): T {
    const element = document.querySelector<T>(selector);
    if (!element) throw new Error(`Missing element: ${selector}`);
    return element;
  }
}
